import { useCallback, useEffect, useState } from 'react'

const STORAGE_KEY = 'sidebar.collapsedSections'

type CollapsedMap = Record<string, unknown>

type UseCollapsedSections = [boolean, () => void, (value: unknown) => void]

function readMap(): CollapsedMap {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return {}
    const parsed: unknown = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? (parsed as CollapsedMap) : {}
  } catch {
    return {}
  }
}

function writeMap(map: CollapsedMap): void {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(map)) } catch { /* storage unavailable */ }
}

export default function useCollapsedSections(sectionKey: string, defaultOpen = true): UseCollapsedSections {
  const [open, setOpen] = useState<boolean>(() => {
    const map = readMap()
    return Object.prototype.hasOwnProperty.call(map, sectionKey) ? !!map[sectionKey] : defaultOpen
  })

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY) return
      const map = readMap()
      if (Object.prototype.hasOwnProperty.call(map, sectionKey)) setOpen(!!map[sectionKey])
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [sectionKey])

  const toggle = useCallback(() => {
    setOpen(prev => {
      const next = !prev
      const map = readMap()
      map[sectionKey] = next
      writeMap(map)
      return next
    })
  }, [sectionKey])

  const set = useCallback((value: unknown) => {
    setOpen(() => {
      const next = !!value
      const map = readMap()
      map[sectionKey] = next
      writeMap(map)
      return next
    })
  }, [sectionKey])

  return [open, toggle, set]
}
